// React and hooks
import React, { useEffect, useRef, useState } from 'react'

// Third-party libraries
import { Col, Form, Row } from 'react-bootstrap'

// Custom components
import { Button } from '@components/common'
import AssignFormsModal from '@components/pages/FormBuilder/SendingFormsTable/AssignFormsModal.component'

// Relative imports
import { AppointmentFormData } from './Appointment.modal'
import {
  SHOW_APPOINTMENT_LAB_ORDERS,
  SHOW_APPOINTMENT_TEMPLATE,
} from './Appointment.constant'
import type { AppointmentNoteOverlayInput } from '@pages/Appointment/appointmentNoteOverlay.util'

import documentIcon from '@assets/images/document_2_fill.svg'
// Relative imports
import { ILabCase, ITemplate } from '@dataTypes/appointment.type'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import { getAllTemplates } from '@services/api/template.api'
import { getAllLabCases } from '@services/api/appointment.api'
import { getFormTemplates } from '@services/api/forms.api'
import { INote, NoteContextType } from '@dataTypes/note.type'
import NoteTextEditor from '@components/common/NoteTextEditor/NoteTextEditor.component'
import type { NoteUpdateItem } from '@components/common/NoteTextEditor/NoteTextEditor.types'

import { logger } from '@utils/safeLogger'

type FormsProps = {
  formData: Partial<AppointmentFormData>
  handleChange: (
    field: string,
    value: string | number | Date | boolean | any[],
  ) => void
  handleFileUpload: (files: FileList | null) => void
  handleDeleteFile: (index: number) => void
  onViewNote?: (input: AppointmentNoteOverlayInput) => void
  appointmentId?: string
  mergeAppointmentSavedNote?: (note: INote) => void
}

const Forms: React.FC<FormsProps> = ({
  formData,
  handleChange,
  handleFileUpload,
  handleDeleteFile,
  onViewNote,
  appointmentId,
  mergeAppointmentSavedNote,
}) => {
  const [templates, setTemplates] = useState<ITemplate[]>([])
  const [labs, setLabs] = useState<ILabCase[]>([])
  const [showAddFormsModal, setShowAddFormsModal] = useState(false)
  const [formTemplateTitleById, setFormTemplateTitleById] = useState<
    Record<string, string>
  >({})
  const [titlesFetchPending, setTitlesFetchPending] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAddFormClick: () => void = () => {
    setShowAddFormsModal(true)
  }

  const handleUploadFileClick: () => void = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleAddFormsToAppointment = (
    selectedFormIds: string[],
    titleById: Record<string, string>,
  ): void => {
    const existing = formData.assignedFormIds ?? []
    const merged = Array.from(new Set([...existing, ...selectedFormIds]))
    handleChange('assignedFormIds', merged)
    setFormTemplateTitleById((prev) => ({ ...prev, ...titleById }))
  }

  const handleRemoveAssignedForm = (formId: string): void => {
    const current = formData.assignedFormIds ?? []
    handleChange(
      'assignedFormIds',
      current.filter((id) => id !== formId),
    )
  }

  const assignedForms = (formData.assignedFormIds ?? []).map((id) => {
    const resolved = formTemplateTitleById[id]
    const displayName =
      resolved ?? (titlesFetchPending ? 'Loading…' : 'Untitled form')
    return { id, displayName }
  })

  const fetchTemplates = async (): Promise<void> => {
    try {
      const response = await getAllTemplates()
      if (response.statusCode === 200) {
        setTemplates(response.data?.templates ?? [])
      } else {
        setTemplates([])
      }
    } catch (error) {
      logger.error('Error fetching treatment plan:', error)
      setTemplates([])
    }
  }

  const fetchLabCases = async (): Promise<void> => {
    try {
      const response = await getAllLabCases(formData.patientId)
      if (response.statusCode === 200) {
        setLabs(response.data?.lab_cases ?? [])
      } else {
        setLabs([])
      }
    } catch (error) {
      logger.error('Error fetching treatment plan:', error)
      setLabs([])
    }
  }

  useEffect(() => {
    if (SHOW_APPOINTMENT_TEMPLATE) {
      void fetchTemplates()
    }
    if (SHOW_APPOINTMENT_LAB_ORDERS) {
      void fetchLabCases()
    }
  }, [formData.patientId])

  useEffect(() => {
    const ids = formData.assignedFormIds ?? []
    if (ids.length === 0) {
      setFormTemplateTitleById({})
      setTitlesFetchPending(false)
      return
    }
    let cancelled = false
    setTitlesFetchPending(true)
    getFormTemplates({ status: FormTemplateStatus.Approved })
      .then((formTemplates) => {
        if (cancelled) return
        const next: Record<string, string> = {}
        for (const t of formTemplates) {
          if (t.form_template_id) {
            next[t.form_template_id] = (t.title ?? '').trim() || 'Untitled form'
          }
        }
        setFormTemplateTitleById((prev) => ({ ...prev, ...next }))
      })
      .finally(() => {
        if (!cancelled) setTitlesFetchPending(false)
      })
    return (): void => {
      cancelled = true
    }
  }, [formData.assignedFormIds])

  const handleUpdateNotes = (data: NoteUpdateItem[]): void => {
    handleChange('notes', data as Partial<INote>[])
  }

  return (
    <div className="mt-2 appt-modal forms">
      {SHOW_APPOINTMENT_LAB_ORDERS ? (
        <Row className="mb-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label>
                Lab orders
                <small className="text-optional ms-1">(Optional)</small>
              </Form.Label>
              <div className="tag-input-container">
                {(formData.labOrders ?? []).map((lab) => (
                  <div key={lab.lab_case_id} className="tag-item me-2">
                    {labs.find((l) => l.lab_case_id === lab.lab_case_id)
                      ?.instructions || ''}
                    <button
                      type="button"
                      className="unstyled-button tag-item-close ti ti-x"
                      aria-label="Remove lab order"
                      onClick={() =>
                        handleChange(
                          'labOrders',
                          (formData.labOrders ?? []).filter(
                            (l) => l.lab_case_id !== lab.lab_case_id,
                          ),
                        )
                      }
                    />
                  </div>
                ))}

                <Form.Select
                  value=""
                  onChange={(e) => {
                    const selected = labs.find(
                      (lab) => lab.lab_case_id === e.target.value,
                    )
                    if (!selected) return
                    const newList = [
                      ...(formData.labOrders ?? []),
                      {
                        lab_case_id: selected.lab_case_id,
                        instructions: selected.instructions,
                      },
                    ]
                    handleChange('labOrders', newList)
                  }}
                  className="tag-input flex-grow-1 border-0 shadow-none w-auto"
                >
                  <option value="">Select lab order</option>
                  {labs
                    .filter(
                      (item) =>
                        !(formData.labOrders ?? []).some(
                          (lab) => lab.lab_case_id === item.lab_case_id,
                        ),
                    )
                    .map((item) => (
                      <option key={item.lab_case_id} value={item.lab_case_id}>
                        {item.instructions}
                      </option>
                    ))}
                </Form.Select>

                {(formData.labOrders?.length ?? 0) > 0 && (
                  <Button
                    className="button-link text-primary ms-2"
                    onClick={() => handleChange('labOrders', [])}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </Form.Group>
          </Col>
        </Row>
      ) : null}
      {SHOW_APPOINTMENT_TEMPLATE ? (
        <Row className="mb-2 gx-2">
          <Col md="12">
            <Form.Group className="">
              <Form.Label>
                Template<small className="text-optional ms-1">(Optional)</small>
              </Form.Label>
              <Form.Select
                value={formData.templateId || ''}
                onChange={(e) => handleChange('templateId', e.target.value)}
              >
                <option value="">Select template</option>
                {templates?.map((temp) => (
                  <option key={temp.template_id} value={temp.template_id}>
                    {temp.template_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      ) : null}
      <Row className="mb-2">
        <Col className="d-flex justify-content-between align-items-center">
          <div className="add-file-title">Add the necessary forms</div>
          <Form.Group
            controlId="formFile"
            className="d-flex align-items-center gap-2"
          >
            <Button
              size="md"
              className="button-secondary add-form-button"
              onClick={handleAddFormClick}
              type="button"
            >
              Add form
            </Button>
            <Button
              size="md"
              className="button-link p-0"
              onClick={handleUploadFileClick}
              type="button"
              style={{ display: 'none' }}
            >
              Upload file
            </Button>
            <Form.Control
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) =>
                handleFileUpload((e.target as HTMLInputElement).files)
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <AssignFormsModal
        show={showAddFormsModal}
        onHide={() => setShowAddFormsModal(false)}
        patientName={formData.patientName ?? ''}
        patientId={formData.patientId}
        variant="addToAppointment"
        onAddFormsToAppointment={handleAddFormsToAppointment}
      />
      {/* Row for Assigned Forms (from Add form modal) */}
      {assignedForms.length > 0 && (
        <Row className="gx-2">
          <Col md="12">
            <div className="list-group">
              {assignedForms.map((form) => (
                <div
                  key={form.id}
                  className="forms-list-container lgi d-flex gap-2 mb-2 
                  rounded-3 align-items-center"
                >
                  <img
                    src={documentIcon}
                    alt="Document icon"
                    className="fs-1 text-blue-300"
                    style={{ width: '24px', height: '24px' }}
                  />
                  <div className="d-flex gap-1 w-100 justify-content-between align-items-center">
                    <div>
                      <div className="appt-file-name">{form.displayName}</div>
                    </div>
                    <button
                      type="button"
                      className="unstyled-button ti ti-circle-x-filled fs-2 text-gray-500"
                      onClick={() => handleRemoveAssignedForm(form.id)}
                      aria-label={`Remove ${form.displayName}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      )}
      {/* Row for File List */}
      <Row className="mb-2 gx-2">
        <Col md="12">
          {formData.files && formData.files.length > 0 && (
            <div className="list-group mb-1">
              {formData.files.map((file, index) => (
                <div
                  key={index}
                  className="forms-list-container lgi d-flex gap-2 mb-2 
                  rounded-3 align-items-center"
                >
                  <img
                    src={documentIcon}
                    alt="Document icon"
                    className="fs-1 text-blue-300"
                    style={{ width: '24px', height: '24px' }}
                  />
                  <div className="d-flex gap-1 w-100 justify-content-between align-items-center">
                    <div>
                      <div className="appt-file-name">{file.name}</div>
                      <div className="appt-file-size">{file.size}</div>
                    </div>
                    <i
                      className="ti ti-circle-x-filled fs-2 text-gray-500"
                      onClick={() => handleDeleteFile(index)}
                    ></i>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>

      <NoteTextEditor
        patientId={formData.patientId!}
        notesOverlayPortalTarget="body"
        savedNotesAllowDelete={false}
        savedNotesUsePdfDownload={true}
        savedNotesPatientDisplayName={formData.patientName ?? ''}
        noteData={{
          tag: NoteContextType.APPOINTMENT,
        }}
        notes={
          formData.notes?.map((note) => ({
            ...note,
            context_id: note.context_id ?? undefined,
          })) ?? []
        }
        onUpdate={handleUpdateNotes}
        onAddNote={
          onViewNote && appointmentId && formData.patientId
            ? () =>
                onViewNote({
                  appointmentId,
                  patientId: formData.patientId!,
                  contextName:
                    `${formData.patientName ?? ''} - ${formData.appointmentTypeName ?? 'Appointment'}`.trim(),
                  onNotesSync: handleUpdateNotes,
                  ...(mergeAppointmentSavedNote != null
                    ? { onNoteSaved: mergeAppointmentSavedNote }
                    : {}),
                })
            : undefined
        }
        onViewNote={
          onViewNote && appointmentId && formData.patientId
            ? (row) =>
                (() => {
                  const payload: AppointmentNoteOverlayInput = {
                    appointmentId,
                    patientId: formData.patientId!,
                    contextName:
                      `${formData.patientName ?? ''} - ${formData.appointmentTypeName ?? 'Appointment'}`.trim(),
                    noteId:
                      typeof row.note_id === 'string' ? row.note_id : undefined,
                    onNotesSync: handleUpdateNotes,
                    ...(mergeAppointmentSavedNote != null
                      ? { onNoteSaved: mergeAppointmentSavedNote }
                      : {}),
                  }
                  onViewNote(payload)
                })()
            : undefined
        }
        onEditSavedNote={
          onViewNote && appointmentId && formData.patientId
            ? (row) =>
                (() => {
                  const payload: AppointmentNoteOverlayInput = {
                    appointmentId,
                    patientId: formData.patientId!,
                    contextName:
                      `${formData.patientName ?? ''} - ${formData.appointmentTypeName ?? 'Appointment'}`.trim(),
                    noteId:
                      typeof row.note_id === 'string' ? row.note_id : undefined,
                    openInEditMode: true,
                    onNotesSync: handleUpdateNotes,
                    ...(mergeAppointmentSavedNote != null
                      ? { onNoteSaved: mergeAppointmentSavedNote }
                      : {}),
                  }
                  onViewNote(payload)
                })()
            : undefined
        }
      />
    </div>
  )
}

export default Forms
